import { Typography } from '@/ui';

import type { PlatformAdminCancellationEvidence } from '@/features/appointments/types';
import type { FC } from 'react';

interface CancellationEvidenceListProps {
  evidence: PlatformAdminCancellationEvidence[];
}

const proximityLabel = (
  proximity: PlatformAdminCancellationEvidence['proximity'],
): string => {
  if (proximity === 'within_48_hours') return 'Within 48 hours';
  if (proximity === 'outside_48_hours') return 'Outside 48 hours';
  return 'Timing unavailable';
};

const actorLabel = (
  evidence: PlatformAdminCancellationEvidence,
): string | null => {
  if (evidence.actor.role === 'member') return 'Member';
  if (evidence.actor.role === 'coach') return 'Coach';
  if (evidence.actor.role === 'admin') return 'Ops';
  return null;
};

const statusLabel = (evidence: PlatformAdminCancellationEvidence): string => {
  if (!evidence.evidenceAvailable)
    return 'Legacy cancellation evidence unavailable';
  if (evidence.status === 'pending') return 'Cancellation pending';
  if (evidence.status === 'rejected') return 'Cancellation rejected';
  if (evidence.status === 'needs_reconciliation') {
    return 'Cancellation needs reconciliation';
  }
  const actor = actorLabel(evidence);
  return actor ? `Cancelled by ${actor}` : 'Cancellation confirmed';
};

const valueLabel = (value: string | null): string =>
  value
    ? value
        .split('_')
        .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
        .join(' ')
    : 'Unavailable';

// The Ops console records an admin-initiated cancellation as initiator 'admin'.
// The dialog labels that choice "Ops", so the evidence list must not title-case
// the raw wire value into a second name for the same thing.
const initiatorLabel = (
  initiator: PlatformAdminCancellationEvidence['initiator'],
): string => {
  if (initiator === 'admin') return 'Ops';
  return valueLabel(initiator);
};

const evidenceDetails = (evidence: PlatformAdminCancellationEvidence) =>
  [
    ['Actor kind', 'Person'],
    ['Actor role', actorLabel(evidence) ?? 'Unavailable'],
    ['Actor person ID', evidence.actor.personId ?? 'Unavailable'],
    ['Initiator', initiatorLabel(evidence.initiator)],
    ['Source', 'Platform'],
    ['Scope', valueLabel(evidence.scope)],
    ['Affected count', evidence.affectedCount?.toString() ?? 'Unavailable'],
    [
      'Affected range',
      evidence.affectedStartAt && evidence.affectedEndAt
        ? `${evidence.affectedStartAt} to ${evidence.affectedEndAt}`
        : 'Unavailable',
    ],
    ['Requested at', evidence.requestedAt],
    ['Proximity', proximityLabel(evidence.proximity)],
    ['Near-term acknowledged', evidence.nearTermAcknowledged ? 'Yes' : 'No'],
    ['Ops note', evidence.opsNote ?? 'None'],
    ['Operation ID', evidence.operationId ?? 'Unavailable'],
    ['History ID', evidence.historyId ?? 'Unavailable'],
    ['Terminal result', valueLabel(evidence.terminalResult)],
  ] as const;

const CancellationEvidenceList: FC<CancellationEvidenceListProps> = ({
  evidence,
}) => (
  <section
    aria-labelledby="cancellation-evidence-heading"
    className="rounded-lg border border-gray-200 bg-white p-4"
  >
    <Typography id="cancellation-evidence-heading" variant="subtitle1">
      Cancellation evidence
    </Typography>
    <ul aria-label="Cancellation evidence" className="mt-2 space-y-2">
      {evidence.map((record) => (
        <li
          key={
            record.operationId
              ? `operation:${record.operationId}`
              : `history:${record.historyId ?? record.requestedAt}`
          }
          className="rounded border border-gray-100 bg-gray-50 px-3 py-2"
        >
          <Typography color="textSecondary" variant="body2">
            {`${statusLabel(record)} · ${proximityLabel(record.proximity)}`}
          </Typography>
          <dl className="mt-1 grid grid-cols-2 gap-x-3 text-xs">
            {evidenceDetails(record).map(([label, value]) => (
              <div key={label} className="contents">
                <dt className="font-medium text-gray-500">{label}</dt>
                <dd className="break-words text-gray-700">{value}</dd>
              </div>
            ))}
          </dl>
          {record.rejection && (
            <Typography color="error" variant="caption">
              {record.rejection.message}
            </Typography>
          )}
          {record.reconciliationReason && (
            <Typography color="error" variant="caption">
              Manual reconciliation required
            </Typography>
          )}
        </li>
      ))}
    </ul>
  </section>
);

export default CancellationEvidenceList;
